import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { Logger } from '@/shared/logger/logger';

import { BrowserStorageApi } from '../apis/browser_storage_api/browser_storage_api';
import { StorageHandler } from './storage_handler';

type CacheWrapper<T> = {
  data: T;
  expiration: number;
};

export class RecacheStorageHandler<T> extends StorageHandler<CacheWrapper<T>> {
  private readonly _cacheDuration: number;
  private readonly _recacheCallback: () => Promise<T>;
  private _currentFetchRequest: Promise<T> | null;

  constructor(
    storageNameSpace: string,
    defaultData: T,
    storageProvider: EBrowserStorageType,
    cacheDuration: number,
    recacheCallback: () => Promise<T>,
  ) {
    super(
      storageNameSpace,
      { data: defaultData, expiration: 0 },
      storageProvider,
    );

    if (
      storageProvider !== EBrowserStorageType.LOCALSTORAGE &&
      storageProvider !== EBrowserStorageType.SESSIONSTORAGE
    ) {
      throw new Error(
        'Recaching is only supported for LOCALSTORAGE and SESSIONSTORAGE.',
      );
    }

    if (!recacheCallback || !cacheDuration) {
      throw new Error('Recache callback and cache duration must be provided');
    }

    this._cacheDuration = cacheDuration;
    this._recacheCallback = recacheCallback;
    this._currentFetchRequest = null;
  }

  override set(data: T | any): boolean {
    const wrappedData: CacheWrapper<T> = {
      data: data,
      expiration: Date.now() + this._cacheDuration,
    };

    return super.set(wrappedData);
  }

  protected override fetchData(): CacheWrapper<T> {
    const result = BrowserStorageApi.get_value({
      default: this._jSONHelper.safeStringify(this._defaultData, ''),
      item_key: this._storageNameSpace,
      storage_provider: this._storageProvider,
    });

    if (result.success) {
      try {
        return this._jSONHelper.safeUnstringify(result.item_value, {
          data: this._defaultData.data,
          expiration: 0,
        });
      } catch {
        Logger.info('[ERROR] Failed to parse data');
        return { data: this._defaultData.data, expiration: 0 };
      }
    } else {
      return { data: this._defaultData.data, expiration: 0 };
    }
  }

  async getAsync(): Promise<T> {
    let wrappedData = this.get();

    if (
      wrappedData.data === this._defaultData.data ||
      Date.now() >= wrappedData.expiration
    ) {
      if (this._currentFetchRequest) return this._currentFetchRequest;
      try {
        this._currentFetchRequest = this._recacheCallback();
        const newData = await this._currentFetchRequest;
        this._currentFetchRequest = null;
        this.set(newData);
        return newData;
      } catch (error) {
        Logger.info('[ERROR] Failed to recache data:', error);
        this._currentFetchRequest = null;
        return wrappedData.data;
      }
    }

    return wrappedData.data;
  }

  override get(): CacheWrapper<T> {
    if (!this._dataFetched) {
      return this.fetchData();
    }
    return super.get();
  }

  getData(): T {
    return this.get().data;
  }
}
